import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Typography,
  TextField,
  RadioGroup,
  Radio,
  FormLabel,
  FormGroup,
  Divider,
  Snackbar,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useUIStore } from '../../../shared/stores/useUIStore';
import {
  useMenuConfiguration,
  useInitializeSharedParameters,
  useCustomSharedParameterFile,
} from '../../../shared/lib';
import { useSharedParameterStore } from '../../../shared/stores/useSharedParameterStore';
import {
  useParameterWorkflowStore,
  type RevitCategory,
} from '../../../shared/stores/useParameterWorkflowStore';
import { borderRadius, layoutGap } from '../../../theme/theme';
import MainLayout from '../../../shared/ui/layout/MainLayout';
import ContentArea from '../../../shared/ui/layout/ContentArea';
import AccordionSection from '../../../shared/ui/layout/AccordionSection';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import {
  ActiveSectionHeader,
  EditorSurface,
  SectionSummaryTile,
  WorkflowNotice,
  WorkflowStepShell,
} from '../../../shared/ui/workflow/WorkflowStepComponents';
import WorkflowFooter from '../../../shared/ui/workflow/WorkflowFooter';
import {
  workflowFeatureTooltips,
  workflowSetupTooltips,
} from '../../../shared/config/wizardTooltips';
import {
  workflowOptions,
  workflowOptionLookup,
  type WorkflowOptionValue,
} from '../../../shared/config/workflowUi';
import { SYSTEM_GROUPING_OPTIONS } from '../../../shared/config';

type Option =
  | 'Iso Drawings Generation'
  | 'Pressure Drop Calculations'
  | 'Stress Calculations'
  | null;

type RequiredFieldKey =
  | 'objectTypeValue'
  | 'sKeyValue'
  | 'pipingSpecValue'
  | 'systemGroupingValue';

const DEFAULT_SHARED_PARAMETER_FILE = 'PipeEX_SharedParameters.txt';

const VALID_SYSTEM_GROUPING_VALUES = new Set(SYSTEM_GROUPING_OPTIONS.map((option) => option.value));
const WORKFLOW_CATEGORIES: RevitCategory[] = ['PIPES', 'PIPE FITTINGS', 'PIPE ACCESSORIES', 'GENERIC MODELS', 'MECHANICAL EQUIPMENT'];

const REQUIRED_FIELDS_BY_WORKFLOW: Record<WorkflowOptionValue, { key: RequiredFieldKey; label: string }[]> = {
  'Iso Drawings Generation': [
    { key: 'objectTypeValue', label: 'OBJECT-TYPE' },
    { key: 'sKeyValue', label: 'SKEY' },
    { key: 'pipingSpecValue', label: 'PIPING-SPEC' },
    { key: 'systemGroupingValue', label: 'SYSTEM GROUPING' },
  ],
  'Pressure Drop Calculations': [
    { key: 'objectTypeValue', label: 'OBJECT-TYPE' },
    { key: 'sKeyValue', label: 'SKEY' },
    { key: 'systemGroupingValue', label: 'SYSTEM GROUPING' },
  ],
  'Stress Calculations': [
    { key: 'objectTypeValue', label: 'OBJECT-TYPE' },
    { key: 'sKeyValue', label: 'SKEY' },
    { key: 'systemGroupingValue', label: 'SYSTEM GROUPING' },
  ],
};

const CATEGORY_ROW_META: Record<RevitCategory, string> = {
  PIPES: 'Primary pipe runs',
  'PIPE FITTINGS': 'Elbows, tees, reducers',
  'PIPE ACCESSORIES': 'Inline accessories',
  'GENERIC MODELS': 'OBJECT-TYPE extension',
  'MECHANICAL EQUIPMENT': 'Equipment families',
};

interface MenuPageProps {
  onContinueClick: () => void;
}

const MenuPage: React.FC<MenuPageProps> = ({ onContinueClick }) => {
  const theme = useTheme();
  const isShortWindow = useMediaQuery('(max-height:760px)');
  const selectedOption = useUIStore((state) => state.selectedOption);
  const selectWorkflowFromMenu = useUIStore((state) => state.selectWorkflowFromMenu);
  const { config: menuConfig, updateField: updateMenuField } = useMenuConfiguration();

  const { isParsed: sharedParamsParsed } = useInitializeSharedParameters();
  const {
    handleFileSelect: handleCustomFileSelect,
    switchToDefaultFile,
    error: sharedParamsError,
  } = useCustomSharedParameterFile();

  const parsedParameters = useSharedParameterStore((state) => state.parsedParameters);
  const sharedParamFilePath = useSharedParameterStore((state) => state.filePath);
  const sharedParamFileContent = useSharedParameterStore((state) => state.fileContent);
  const useDefaultFile = useSharedParameterStore((state) => state.useDefaultFile);
  const setUseDefaultFile = useSharedParameterStore((state) => state.setUseDefaultFile);
  const setFilePath = useSharedParameterStore((state) => state.setFilePath);
  const workflowMode = useParameterWorkflowStore((state) => state.workflowMode);
  const workflowSharedParameterFilePath = useParameterWorkflowStore(
    (state) => state.sharedParameterFilePath
  );
  const workflowUseDefaultFile = useParameterWorkflowStore((state) => state.useDefaultFile);
  const workflowSystemGroupingPreference = useParameterWorkflowStore(
    (state) => state.systemGroupingPreference
  );
  const workflowSelectedCategories = useParameterWorkflowStore(
    (state) => state.selectedCategories
  );
  const setWorkflowMode = useParameterWorkflowStore((state) => state.setWorkflowMode);
  const setCategorySelected = useParameterWorkflowStore((state) => state.setCategorySelected);
  const setWorkflowSharedParameterFilePath = useParameterWorkflowStore(
    (state) => state.setSharedParameterFilePath
  );
  const setWorkflowUseDefaultFile = useParameterWorkflowStore((state) => state.setUseDefaultFile);
  const setWorkflowSystemGroupingPreference = useParameterWorkflowStore(
    (state) => state.setSystemGroupingPreference
  );

  const [paramFileExpanded, setParamFileExpanded] = React.useState(false);
  const [paramConfigExpanded, setParamConfigExpanded] = React.useState(false);
  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [showWorkflowChooser, setShowWorkflowChooser] = React.useState(!selectedOption);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedCategoriesSet = useMemo(
    () => new Set(workflowSelectedCategories),
    [workflowSelectedCategories]
  );
  const selectedCategories = useMemo<Record<RevitCategory, boolean>>(
    () =>
      WORKFLOW_CATEGORIES.reduce((nextState, category) => {
        nextState[category] = selectedCategoriesSet.has(category);
        return nextState;
      }, {} as Record<RevitCategory, boolean>),
    [selectedCategoriesSet]
  );
  const customFilePath =
    !useDefaultFile && sharedParamFileContent ? sharedParamFilePath : '';

  useEffect(() => {
    if (workflowMode !== 'multipage') {
      setWorkflowMode('multipage');
    }
  }, [setWorkflowMode, workflowMode]);

  useEffect(() => {
    if (workflowUseDefaultFile !== useDefaultFile) {
      setWorkflowUseDefaultFile(useDefaultFile);
    }

    const nextWorkflowFilePath = useDefaultFile
      ? DEFAULT_SHARED_PARAMETER_FILE
      : sharedParamFileContent
        ? sharedParamFilePath
        : '';

    if (workflowSharedParameterFilePath !== nextWorkflowFilePath) {
      setWorkflowSharedParameterFilePath(nextWorkflowFilePath);
    }
  }, [
    setWorkflowSharedParameterFilePath,
    setWorkflowUseDefaultFile,
    sharedParamFileContent,
    sharedParamFilePath,
    useDefaultFile,
    workflowSharedParameterFilePath,
    workflowUseDefaultFile,
  ]);

  useEffect(() => {
    if (workflowSystemGroupingPreference !== menuConfig.systemGroupingValue) {
      setWorkflowSystemGroupingPreference(menuConfig.systemGroupingValue);
    }
  }, [
    menuConfig.systemGroupingValue,
    setWorkflowSystemGroupingPreference,
    workflowSystemGroupingPreference,
  ]);

  useEffect(() => {
    if (!selectedOption) {
      setShowWorkflowChooser(true);
    }
  }, [selectedOption]);

  const handleCategoryToggle = useCallback((category: RevitCategory) => {
    setCategorySelected(category, !selectedCategories[category]);
  }, [selectedCategories, setCategorySelected]);

  const handleWorkflowSelect = useCallback((value: WorkflowOptionValue) => {
    setWorkflowMode('multipage');
    selectWorkflowFromMenu(value);
    updateMenuField({ selectedOption: value });
    setShowWorkflowChooser(false);
  }, [selectWorkflowFromMenu, setWorkflowMode, updateMenuField]);

  const handleObjectTypeChange = useCallback((value: string) => {
    updateMenuField({ objectTypeValue: value });
  }, [updateMenuField]);

  const handleSKeyChange = useCallback((value: string) => {
    updateMenuField({ sKeyValue: value });
  }, [updateMenuField]);

  const handlePipingSpecChange = useCallback((value: string) => {
    updateMenuField({ pipingSpecValue: value });
  }, [updateMenuField]);

  const handleSystemGroupingChange = useCallback((value: string) => {
    updateMenuField({ systemGroupingValue: value });
  }, [updateMenuField]);

  const handleDefaultFileSelected = useCallback(() => {
    switchToDefaultFile();
    setSnackbarMessage('Setup selections were reset to the PipeEX defaults.');
    setShowSnackbar(true);
  }, [switchToDefaultFile]);

  const handleFileSelectionModeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const shouldUseDefault = event.target.value === 'default';
    if (shouldUseDefault) {
      handleDefaultFileSelected();
      return;
    }

    setUseDefaultFile(false);
  }, [setUseDefaultFile, handleDefaultFileSelected]);

  const handleBrowseFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFilePath(file.name);
    }
    handleCustomFileSelect(event);
  }, [handleCustomFileSelect, setFilePath]);

  const requiredFieldPlan = selectedOption
    ? REQUIRED_FIELDS_BY_WORKFLOW[selectedOption]
    : [];
  const selectedWorkflowMeta = selectedOption
    ? workflowOptionLookup[selectedOption]
    : null;
  const fieldValues: Record<RequiredFieldKey, string> = {
    objectTypeValue: menuConfig.objectTypeValue,
    sKeyValue: menuConfig.sKeyValue,
    pipingSpecValue: menuConfig.pipingSpecValue,
    systemGroupingValue: menuConfig.systemGroupingValue,
  };
  const missingRequiredFields = requiredFieldPlan.filter((field) => !fieldValues[field.key].trim());
  const trimmedSystemGroupingValue = menuConfig.systemGroupingValue.trim();
  const systemGroupingInvalid =
    trimmedSystemGroupingValue.length > 0 &&
    !VALID_SYSTEM_GROUPING_VALUES.has(trimmedSystemGroupingValue);
  const selectedCategoryCount = workflowSelectedCategories.length;
  const loadedParameterCount = parsedParameters.length;
  const hasLoadedSharedParameters = useDefaultFile
    ? sharedParamsParsed && loadedParameterCount > 0
    : Boolean(sharedParamFileContent) && loadedParameterCount > 0;
  const isContinueDisabled =
    !selectedOption ||
    missingRequiredFields.length > 0 ||
    systemGroupingInvalid;

  const sharedParameterSummary = hasLoadedSharedParameters
    ? `${loadedParameterCount} shared parameters available`
    : useDefaultFile
      ? 'PipeEX shared parameter file selected'
      : customFilePath
        ? 'Custom shared parameter file selected'
        : 'Manual entry only';
  const sharedParameterDetail = hasLoadedSharedParameters
    ? sharedParamFilePath || DEFAULT_SHARED_PARAMETER_FILE
    : useDefaultFile
      ? DEFAULT_SHARED_PARAMETER_FILE
      : customFilePath || 'Load a file if you want dropdown-backed parameter selection.';
  const targetCategoriesSummary = `${selectedCategoryCount}/${WORKFLOW_CATEGORIES.length} Revit categories selected`;
  const footerSummaryText = !selectedOption
    ? 'Continue blocked: select a workflow'
    : missingRequiredFields.length > 0
      ? `Continue blocked: ${missingRequiredFields.length} required default${missingRequiredFields.length === 1 ? '' : 's'} missing`
      : systemGroupingInvalid
        ? 'Continue blocked: choose BySystemName or BySystemTypeName for system grouping'
        : 'Ready to continue';
  const isReadyToContinue =
    Boolean(selectedOption) &&
    missingRequiredFields.length === 0 &&
    !systemGroupingInvalid;
  const pageSubtitle = !selectedOption
    ? 'Choose the engineering outcome, then map its required PCF defaults.'
    : `Map the required PCF defaults for ${selectedWorkflowMeta?.shortLabel}, then continue.`;

  // Parameter creation deliberately does NOT run on this step: the connector
  // binding page owns the mode selection, and its Continue applies the batch
  // with the mode the user picked there.
  const handleContinue = useCallback(() => {
    if (!selectedOption) {
      setSnackbarMessage('Choose a workflow before continuing.');
      setShowSnackbar(true);
      return;
    }

    if (missingRequiredFields.length > 0) {
      setSnackbarMessage(`Complete the required mapping defaults: ${missingRequiredFields.map((field) => field.label).join(', ')}`);
      setShowSnackbar(true);
      return;
    }

    if (systemGroupingInvalid) {
      setSnackbarMessage('SYSTEM GROUPING must be one of the two supported options: BySystemName or BySystemTypeName. Select a value from the dropdown.');
      setShowSnackbar(true);
      return;
    }

    onContinueClick();
  }, [missingRequiredFields, onContinueClick, selectedOption, systemGroupingInvalid]);

  const handleCloseSnackbar = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  // Advanced options accordions sit inside the section's EditorSurface: keep
  // their card border/background, but match the surface corner radius and let
  // the parent grid own the spacing (the accordion's own margins would
  // double up and the larger default radius fights the outer card).
  const advancedNestedCardSx = {
    mb: 0,
    borderRadius: `${borderRadius.md}px !important`,
    '&.Mui-expanded': {
      margin: 0,
    },
  } as const;

  return (
    <MainLayout
      title="Choose the workflow"
      subtitle={pageSubtitle}
      maxWidth={840}
      maxHeight={1180}
      showNext={true}
      onNext={handleContinue}
      nextText="Continue"
      nextDisabled={isContinueDisabled}
      ariaLabel="Workflow setup screen"
      compactWorkflowChrome={true}
      workflowChromeVariant="wizard"
      footerContent={
        <WorkflowFooter 
          isReady={isReadyToContinue} 
          readyText={footerSummaryText}
          blockedReasons={isReadyToContinue ? [] : [footerSummaryText]}
        />
      }
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        accept=".txt,.sharedparameters"
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      <WorkflowStepShell
        sx={{
          gap: layoutGap.row,
          pb: isShortWindow ? layoutGap.micro : layoutGap.dense,
        }}
      >
        {selectedOption && !showWorkflowChooser && (
          <EditorSurface sx={{ p: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: layoutGap.row,
                flexWrap: 'wrap',
              }}
            >
              <ActiveSectionHeader
                title={selectedWorkflowMeta?.shortLabel || 'Selected workflow'}
                description={selectedWorkflowMeta?.summary || 'Review the required PCF defaults for this workflow.'}
                statusLabel="Set"
                statusTone="set"
                aside={
                  <HelpTooltipTrigger
        label="Current workflow summary"
                    help={workflowSetupTooltips.selectedWorkflow}
                  />
                }
              />
              <ActionHelpTooltip help={workflowSetupTooltips.changeWorkflow} placement="bottom-start">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowWorkflowChooser(true)}
                  sx={{ minWidth: 120, minHeight: 28 }}
                >
                  Change workflow
                </Button>
              </ActionHelpTooltip>
            </Box>
          </EditorSurface>
        )}

        {(!selectedOption || showWorkflowChooser) && (
          <EditorSurface sx={{ p: 1, minWidth: 0 }}>
            <ActiveSectionHeader
              title={selectedOption ? 'Change workflow' : 'Choose workflow'}
              description={
                selectedOption
                  ? 'Switch workflows only when the required engineering deliverable has changed.'
                  : 'Choose the engineering deliverable you want PipeEX to prepare this Revit model for.'
              }
              statusLabel={selectedOption ? 'Set' : 'Missing'}
              statusTone={selectedOption ? 'set' : 'missing'}
              aside={
                <HelpTooltipTrigger
        label="Workflow selection"
                  help={workflowSetupTooltips.workflowSelection}
                />
              }
            />

            <Box
              role="radiogroup"
              aria-label="Workflow selection"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: layoutGap.row,
                mt: layoutGap.row,
              }}
            >
              {workflowOptions.map((workflow) => {
                const isSelected = selectedOption === workflow.value;
                const requiredDefaultsCount = REQUIRED_FIELDS_BY_WORKFLOW[workflow.value].length;

                return (
                  <SectionSummaryTile
                    key={workflow.value}
                    title={workflow.shortLabel}
                    detail={workflow.setupNote}
                    meta={workflow.deliverables.join(' • ')}
                    help={workflowFeatureTooltips[workflow.value]}
                    statusLabel={`${requiredDefaultsCount} defaults`}
                    statusTone={isSelected ? 'set' : 'optional'}
                    selected={isSelected}
                    onClick={() => handleWorkflowSelect(workflow.value)}
                    role="radio"
                    ariaChecked={isSelected}
                  />
                );
              })}
            </Box>
          </EditorSurface>
        )}

        {!selectedOption && (
          <WorkflowNotice severity="info">
            After you make a selection, the PCF defaults and setup steps for this workflow will be displayed.
          </WorkflowNotice>
        )}

        {selectedOption && (
          <ContentArea
            selectedOption={selectedOption as Option}
            objectTypeValue={menuConfig.objectTypeValue}
            sKeyValue={menuConfig.sKeyValue}
            pipingSpecValue={menuConfig.pipingSpecValue}
            systemGroupingValue={menuConfig.systemGroupingValue}
            defaultPropertiesConfirmed={false}
            onObjectTypeChange={handleObjectTypeChange}
            onSKeyChange={handleSKeyChange}
            onPipingSpecChange={handlePipingSpecChange}
            onSystemGroupingChange={handleSystemGroupingChange}
            onDefaultPropertiesChange={() => undefined}
            onContinueClick={onContinueClick}
            hideCheckboxAndButton={true}
            showValidationErrors={systemGroupingInvalid}
            validationErrors={
              systemGroupingInvalid
                ? {
                    systemGroupingValue:
                      'Select one of the two options: BySystemName or BySystemTypeName.',
                  }
                : {}
            }
          />
        )}

        <EditorSurface sx={{ p: 1, minWidth: 0 }}>
          <ActiveSectionHeader
            title="Advanced options"
            description="Choose the shared parameter file and the Revit categories that should receive the required mapped parameters."
            statusLabel="Optional"
            statusTone="optional"
          />
          {/* Same inner inset as the Required defaults fields: CompactFieldRow
              adds ~2px around its inputs, so pad the accordion grid to match —
              card border to inner border is then identical in both sections. */}
          <Box sx={{ display: 'grid', gap: layoutGap.row, mt: layoutGap.row, px: 0.25 }}>
          <ActionHelpTooltip help={workflowSetupTooltips.sharedParameterFileSection} placement="bottom-start">
            <Box sx={{ width: '100%' }}>
              <AccordionSection
                title="Shared parameter file"
                summary={
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" component="div">
                      {sharedParameterSummary}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        opacity: 0.82,
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {hasLoadedSharedParameters
                        ? sharedParameterDetail
                        : useDefaultFile
                          ? 'Default shared parameter file'
                          : customFilePath || 'Manual entry only'}
                    </Typography>
                  </Box>
                }
                expanded={paramFileExpanded}
                onChange={setParamFileExpanded}
                compact={true}
                sx={advancedNestedCardSx}
                summarySx={{
                  minHeight: 32,
                  borderRadius: `${borderRadius.md}px ${borderRadius.md}px 0 0`,
                  '&.Mui-expanded': { minHeight: 32 },
                }}
                detailsSx={{
                  borderRadius: `0 0 ${borderRadius.md}px ${borderRadius.md}px`,
                  py: 0.85,
                  px: 1,
                }}
              >
                <Box sx={{ display: 'grid', gap: layoutGap.row }}>
                  <Typography variant="caption" color="text.secondary">
                    Load shared parameter definitions for searchable lists, or type exact Revit parameter names manually.
                  </Typography>

                  <FormControl component="fieldset" size="small">
                    <FormLabel component="legend">
                      <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
                        Shared parameter file
                      </Typography>
                    </FormLabel>
                    <RadioGroup
                      value={useDefaultFile ? 'default' : 'custom'}
                      onChange={handleFileSelectionModeChange}
                      sx={{ gap: layoutGap.dense }}
                    >
                      <FormControlLabel
                        value="default"
                        control={<Radio size="small" />}
                        onClick={handleDefaultFileSelected}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Use the default PipeEX shared parameter file
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {DEFAULT_SHARED_PARAMETER_FILE}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          alignItems: 'flex-start',
                          m: 0,
                          px: 0.45,
                          py: 0.35,
                          borderRadius: `${borderRadius.md}px`,
                          backgroundColor: alpha(theme.palette.background.default, 0.12),
                        }}
                      />
                      <FormControlLabel
                        value="custom"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Choose a custom shared parameter file
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Load a different shared parameter file for this setup session.
                            </Typography>
                          </Box>
                        }
                        sx={{
                          alignItems: 'flex-start',
                          m: 0,
                          px: 0.45,
                          py: 0.35,
                          borderRadius: `${borderRadius.md}px`,
                          backgroundColor: alpha(theme.palette.background.default, 0.12),
                        }}
                      />
                    </RadioGroup>
                  </FormControl>

                  {!useDefaultFile && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                        gap: layoutGap.row,
                        alignItems: 'start',
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label="Selected custom file"
                        value={customFilePath}
                        placeholder="No custom file selected yet"
                        helperText="Select a shared parameter file with Browse. This path is read-only."
                        InputProps={{ readOnly: true }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleBrowseFile}
                        sx={{ minWidth: '92px', minHeight: 36 }}
                      >
                        Browse
                      </Button>
                    </Box>
                  )}

                  {sharedParamsError && (
                    <WorkflowNotice severity="error">
                      {sharedParamsError}
                    </WorkflowNotice>
                  )}

                  {hasLoadedSharedParameters && (
                    <Box
                      sx={{
                        px: 0.85,
                        py: 0.55,
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.16)}`,
                        borderRadius: `${borderRadius.md}px`,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: theme.palette.success.light }}>
                        Loaded {loadedParameterCount} parameters from {sharedParamFilePath || DEFAULT_SHARED_PARAMETER_FILE}.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionSection>
            </Box>
          </ActionHelpTooltip>

          <ActionHelpTooltip help={workflowSetupTooltips.targetCategoriesSection} placement="bottom-start">
            <Box sx={{ width: '100%' }}>
              <AccordionSection
                title="Revit target categories"
                summary={
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" component="div">
                      {targetCategoriesSummary}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        opacity: 0.82,
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      Required mappings target piping categories. OBJECT-TYPE can also target Generic Models and Mechanical Equipment.
                    </Typography>
                  </Box>
                }
                expanded={paramConfigExpanded}
                onChange={setParamConfigExpanded}
                compact={true}
                sx={advancedNestedCardSx}
                summarySx={{
                  minHeight: 32,
                  borderRadius: `${borderRadius.md}px ${borderRadius.md}px 0 0`,
                  '&.Mui-expanded': { minHeight: 32 },
                }}
                detailsSx={{
                  borderRadius: `0 0 ${borderRadius.md}px ${borderRadius.md}px`,
                  py: 0.8,
                  px: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gap: layoutGap.row,
                  }}
                >
                  <Box
                    sx={{
                      position: 'sticky',
                      top: -8,
                      zIndex: 1,
                      px: 0.8,
                      py: 0.6,
                      borderRadius: `${borderRadius.md}px`,
                      bgcolor: alpha(theme.palette.background.default, 0.92),
                      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.primary, fontWeight: 700 }}>
                      {targetCategoriesSummary}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Required mappings target piping categories. OBJECT-TYPE can also target Generic Models and Mechanical Equipment.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gap: layoutGap.row,
                    }}
                  >
                    <Box
                      sx={{
                        p: 0.8,
                        borderRadius: `${borderRadius.md}px`,
                        backgroundColor: alpha(theme.palette.background.default, 0.16),
                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: layoutGap.row, mb: layoutGap.row }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Target categories
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Revit categories the required mapped parameters of this workflow can be added to.
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                          {selectedCategoryCount}/{WORKFLOW_CATEGORIES.length} configured
                        </Typography>
                      </Box>
                      <FormGroup sx={{ gap: layoutGap.dense }}>
                        {WORKFLOW_CATEGORIES.map((category) => (
                          <FormControlLabel
                            key={category}
                            control={
                              <Checkbox
                                checked={Boolean(selectedCategories[category])}
                                onChange={() => handleCategoryToggle(category)}
                                size="small"
                              />
                            }
                            label={
                              <Box sx={{ display: 'grid', gap: layoutGap.micro }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                  {category}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.25 }}>
                                  {CATEGORY_ROW_META[category]}
                                </Typography>
                              </Box>
                            }
                            sx={{
                              m: 0,
                              alignItems: 'flex-start',
                              px: layoutGap.micro,
                              py: 0.2,
                              borderRadius: `${borderRadius.md}px`,
                              '& .MuiFormControlLabel-label': { flex: 1 },
                            }}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 0.1, opacity: 0.35 }} />

                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    These categories define eligible creation targets only. PipeEX carries the required mappings from the section above into later setup steps and creates missing parameters after you confirm the binding mode.
                  </Typography>
                </Box>
              </AccordionSection>
            </Box>
          </ActionHelpTooltip>
          </Box>
        </EditorSurface>
      </WorkflowStepShell>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </MainLayout>
  );
};

export default memo(MenuPage);
